import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@utils/api';
import { useLocation } from '@context/LocationContext';
import { useSocket } from './useSocket';

const REFRESH_INTERVAL = 18000;
const EARTH_RADIUS_KM = 6371;

const toRadians = (value) => (value * Math.PI) / 180;

const distanceKm = (origin, target) => {
  const [lng1, lat1] = origin;
  const [lng2, lat2] = target;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
};

const sortActivitiesByDistance = (activities, location) => {
  if (!location?.lat || !location?.lng || !Array.isArray(activities) || activities.length < 2) {
    return activities;
  }

  const origin = [Number(location.lng), Number(location.lat)];
  return [...activities].sort((a, b) => {
    const coordA = a?.location?.coordinates;
    const coordB = b?.location?.coordinates;
    if (!Array.isArray(coordA) || coordA.length !== 2) return 1;
    if (!Array.isArray(coordB) || coordB.length !== 2) return -1;
    return distanceKm(origin, coordA) - distanceKm(origin, coordB);
  });
};

const buildActivityListParams = (filters) => {
  const params = {};
  if (filters.category) params.category = filters.category;
  if (filters.ageGroup) params.ageGroup = filters.ageGroup;
  const r = Number(filters.radius);
  params.radius = Number.isFinite(r) && r > 0 ? r : 50;
  if (filters.allowedGender) params.allowedGender = filters.allowedGender;
  if (filters.verifiedActivitiesOnly) params.verifiedActivitiesOnly = 'true';
  if (filters.includePast) params.includePast = 'true';
  return params;
};

const mergeExplorePast = (sortedUpcoming, pastFromApi) => {
  if (!Array.isArray(pastFromApi) || pastFromApi.length === 0) return sortedUpcoming;
  const seen = new Set(sortedUpcoming.map((a) => String(a._id)));
  const tagged = pastFromApi
    .filter((a) => a?._id && !seen.has(String(a._id)))
    .map((a) => ({ ...a, _explorePast: true }));
  return [...sortedUpcoming, ...tagged];
};

export const useActivities = (filters = {}) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { location, locationLoading } = useLocation();
  const { on } = useSocket();
  const isMounted = useRef(true);
  const activitiesRef = useRef(activities);

  // IDs just prepended (optional subtle fade-in on FeedPage)
  const [newItemIds, setNewItemIds] = useState(() => new Set());
  const [prependVersion, setPrependVersion] = useState(0);
  const scrollSnapshotRef = useRef(null);
  const fadeTimerRef = useRef(null);

  useEffect(() => { activitiesRef.current = activities; }, [activities]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  const tabVisibleRef = useRef(true);
  useEffect(() => {
    const handler = () => { tabVisibleRef.current = !document.hidden; };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const flashNewIds = useCallback((ids) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    setNewItemIds(idSet);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      if (isMounted.current) setNewItemIds(new Set());
    }, 800);
  }, []);

  const captureScrollSnapshot = () => {
    if (typeof window !== 'undefined') {
      scrollSnapshotRef.current = {
        scrollHeight: document.documentElement.scrollHeight,
        scrollTop: window.scrollY,
      };
    }
  };

  const isInitialFetch = useRef(true);

  // Full replace: mount + filter/location changes + explicit refetch()
  const fetchActivities = useCallback(async () => {
    if (locationLoading) {
      if (isMounted.current && isInitialFetch.current) setLoading(true);
      return;
    }

    try {
      if (isMounted.current && isInitialFetch.current) setLoading(true);
      const params = buildActivityListParams(filters);

      if (location.lat && location.lng) {
        params.lng = location.lng;
        params.lat = location.lat;
      }

      const endpoint = location.lat ? '/activities/nearby' : '/activities';
      const { data } = await api.get(endpoint, { params });
      const sortedActivities = sortActivitiesByDistance(data.activities || [], location);
      const merged = filters.includePast
        ? mergeExplorePast(sortedActivities, data.pastActivities)
        : sortedActivities;

      if (isMounted.current) {
        setActivities(merged);
        setNewItemIds(new Set());
        setError(null);
      }
    } catch (err) {
      try {
        const { data } = await api.get('/activities', { params: buildActivityListParams(filters) });
        if (isMounted.current) {
          const sorted = sortActivitiesByDistance(data.activities || [], location);
          const merged = filters.includePast
            ? mergeExplorePast(sorted, data.pastActivities)
            : sorted;
          setActivities(merged);
          setNewItemIds(new Set());
          setError(null);
        }
      } catch (fallbackErr) {
        if (isMounted.current) {
          setError(fallbackErr.response?.data?.message || 'Failed to load vibes');
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        isInitialFetch.current = false;
      }
    }
  }, [location.lat, location.lng, locationLoading, JSON.stringify(filters)]);

  // Background poll: merge only — no full list reset
  const silentRefresh = useCallback(async () => {
    if (!isMounted.current || !tabVisibleRef.current || locationLoading) return;

    try {
      const params = buildActivityListParams(filters);
      if (location.lat && location.lng) {
        params.lng = location.lng;
        params.lat = location.lat;
      }
      const endpoint = location.lat ? '/activities/nearby' : '/activities';
      const { data } = await api.get(endpoint, { params });
      const sortedUpcoming = sortActivitiesByDistance(data.activities || [], location);
      const fresh = filters.includePast
        ? mergeExplorePast(sortedUpcoming, data.pastActivities)
        : sortedUpcoming;
      if (!isMounted.current || fresh.length === 0) return;

      const currentIds = new Set(activitiesRef.current.map((a) => a._id));
      const incomingNew = fresh.filter((a) => !currentIds.has(a._id));
      const hasNew = incomingNew.length > 0;

      if (hasNew) captureScrollSnapshot();

      setActivities((prev) => {
        if (fresh.length !== prev.length) return fresh;

        const unchanged = fresh.every((item, i) => {
          const old = prev[i];
          if (!old) return false;
          return (
            item._id === old._id &&
            item.title === old.title &&
            item.status === old.status &&
            item.image === old.image &&
            item.participants?.length === old.participants?.length
          );
        });

        return unchanged ? prev : fresh;
      });

      if (hasNew) {
        flashNewIds(incomingNew.map((a) => a._id));
        setPrependVersion((v) => v + 1);
      }
    } catch {
      scrollSnapshotRef.current = null;
    }
  }, [location.lat, location.lng, locationLoading, JSON.stringify(filters), flashNewIds]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    if (loading) return;
    const iv = setInterval(silentRefresh, REFRESH_INTERVAL);
    return () => clearInterval(iv);
  }, [loading, silentRefresh]);

  useEffect(() => {
    const handler = () => {
      if (!document.hidden && !loading && isMounted.current) {
        silentRefresh();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [silentRefresh, loading]);

  useEffect(() => {
    const unsub = on('activity_created', (activity) => {
      if (!isMounted.current) return;

      const alreadyExists = activitiesRef.current.some((a) => a._id === activity._id);
      if (alreadyExists) return;

      flashNewIds([activity._id]);
      setPrependVersion((v) => v + 1);
      silentRefresh();

      const createdId = activity?._id;
      if (createdId) {
        setTimeout(async () => {
          if (!isMounted.current) return;
          try {
            const { data } = await api.get(`/activities/${createdId}`);
            const fresh = data.activity;
            if (!fresh) return;
            setActivities((prev) =>
              prev.map((a) => (a._id === fresh._id ? fresh : a)),
            );
          } catch {
            /* ignore */
          }
        }, 2500);
      }
    });
    return unsub;
  }, [on, flashNewIds, silentRefresh]);

  useEffect(() => {
    const unsub = on('activity_expired', (activityId) => {
      if (!isMounted.current) return;
      setActivities((prev) => prev.filter((a) => a._id !== activityId));
    });
    return unsub;
  }, [on]);

  return {
    activities,
    setActivities,
    loading,
    error,
    refetch: fetchActivities,
    newItemIds,
    prependVersion,
    scrollSnapshotRef,
  };
};

export const useActivity = (id) => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const imageRetryDone = useRef(false);

  useEffect(() => {
    imageRetryDone.current = false;
  }, [id]);

  const fetchActivity = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/activities/${id}`);
      setActivity(data.activity);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vibe');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    if (!activity || imageRetryDone.current) return;
    if (activity.image && String(activity.image).trim() !== '') return;
    imageRetryDone.current = true;
    const t = setTimeout(() => {
      fetchActivity();
    }, 2800);
    return () => clearTimeout(t);
  }, [activity, fetchActivity]);

  return { activity, loading, error, refetch: fetchActivity, setActivity };
};
